import React, { ChangeEvent, useState, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
  FormState,
  FormErrors,
  BusinessPageFormProps,
  CreateOnboardingRequestVariables,
} from '../BusinessPage.types';
import { CategorySelect } from '../BusinessCategorySelect/BusinessPageFormCategorySelect';
import {
  getPhoneError,
  handleChange,
  createPhoneMask,
  validateAndSubmitForm,
  handleCategories,
} from '../BusinessFormHelpers';
import * as S from './BusinessPageForm.styles';
import {
  Category,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { ROUTES } from '@/constants/routes';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { createOnboardingRequest } from '@/api/mutations/business.mutations';
import { rootCategoriesMock } from '@/__e2e__/mock/categoriesMock';
import { useLiteWebView, withAppFlag } from '@/hooks/useDefaultLiteWebView';

export const BusinessPageForm: React.FC<BusinessPageFormProps> = ({
  formElRef,
  setShowResultModal,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const modalEl = useRef<HTMLDivElement>(null);
  const triggerEl = useRef<HTMLDivElement>(null);
  const isLite = useLiteWebView();

  const [createRequest] = useMutation<
    { createOnboardingRequest: { entity: CreateOnboardingRequestVariables } },
    CreateOnboardingRequestVariables
  >(createOnboardingRequest);

  const { data: { categories = [] } = {} } =
    useQuery<GetRootCategoriesResponse>(GET_ROOT_CATEGORIES);

  const [state, setState] = useState<FormState>({
    shop: '',
    name: '',
    categories: [],
    phone: '',
    email: '',
  });
  const [categoriesNamesArr, setCategoriesNamesArr] = useState<string[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({
    shop: '',
    name: '',
    categories: '',
    phone: '',
    email: '',
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleChange(e, setErrors, setState, getPhoneError, t);
  };

  const handleCategoryChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleCategories(e, state, setState, setCategoriesNamesArr, setErrors);
  };

  const handleFormSubmit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    validateAndSubmitForm(
      e,
      errors,
      state,
      setErrors,
      t,
      createRequest,
      setShowResultModal
    );
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      modalEl.current &&
      !modalEl.current.contains(e.target as Node) &&
      !triggerEl.current?.contains(e.target as Node)
    ) {
      setShowCategoryModal(false);
    }
  };

  useEffect(() => {
    if (showCategoryModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryModal]);

  const handleShowCategoryModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCategoryModal(prevState => !prevState);
  };

  const finalCategories = (
    categories.length ? categories : rootCategoriesMock(language).categories
  ) as Category[];

  return (
    <S.BusinessPageForm ref={formElRef}>
      <S.BusinessPageFormTitle>
        {t('business.form.title')}
      </S.BusinessPageFormTitle>
      <S.BusinessPageFormItemsContainer>
        <S.BusinessPageFormItemsGroup>
          <S.BusinessPageFormItems
            id="shop"
            name="shop"
            onChange={handleInputChange}
            pattern="[a-zA-Zа-яА-ЯƏŞÇÖÜĞIİəşçöüğıi' 0-9]*"
            maxLength={50}
            data-stat="landing-form-shop"
            placeholder=" "
            $hasError={!!errors.shop}
            onInput={e => {
              const input = e.target as HTMLInputElement;
              input.value = input.value
                .replace(/[^a-zA-Zа-яА-ЯƏŞÇÖÜĞIİəşçöüğıi' 0-9]/g, '')
                .replace(/\s{2,}/g, ' ')
                .replace(/'{2,}/g, "'");
            }}
          />
          <S.BusinessPageFormLabel htmlFor="shop">
            {t('business.form.shop')}
          </S.BusinessPageFormLabel>
          <S.BusinessPageFormError>{errors.shop}</S.BusinessPageFormError>
        </S.BusinessPageFormItemsGroup>

        <S.BusinessPageCaregories>
          <S.BusinessPageFormItemsGroup
            ref={triggerEl}
            onClick={handleShowCategoryModal}
          >
            <S.BusinessPageFormItemsCategory
              data-testid="business-category"
              data-stat="landing-form-category"
              $showModal={showCategoryModal}
              $showNames={categoriesNamesArr.length > 0}
              $hasError={!!errors.categories}
            >
              <span>{categoriesNamesArr.join(', ')}</span>
            </S.BusinessPageFormItemsCategory>
            <S.BusinessPageFormLabel>
              {t('business.form.category')}
            </S.BusinessPageFormLabel>
            <S.BusinessPageFormError>
              {errors.categories}
            </S.BusinessPageFormError>
          </S.BusinessPageFormItemsGroup>

          <CategorySelect
            categories={finalCategories}
            selectedCategories={state.categories}
            showCategoryModal={showCategoryModal}
            modalEl={modalEl}
            onChange={handleCategoryChange}
            onClose={handleShowCategoryModal}
          />
        </S.BusinessPageCaregories>

        <S.BusinessPageFormItemsGroup>
          <S.BusinessPageFormItems
            id="name"
            name="name"
            onChange={handleInputChange}
            placeholder=" "
            pattern="[a-zA-Zа-яА-ЯƏŞÇÖÜĞIİəşçöüğıi' ]*"
            inputMode="text"
            maxLength={50}
            data-stat="landing-form-name"
            $hasError={!!errors.name}
            onInput={e => {
              const input = e.target as HTMLInputElement;
              input.value = input.value
                .replace(/[^a-zA-Zа-яА-ЯƏŞÇÖÜĞIİəşçöüğıi' ]/g, '')
                .replace(/\s{2,}/g, ' ')
                .replace(/'{2,}/g, "'");
            }}
          />
          <S.BusinessPageFormLabel htmlFor="name">
            {t('business.form.name')}
          </S.BusinessPageFormLabel>
          <S.BusinessPageFormError>{errors.name}</S.BusinessPageFormError>
        </S.BusinessPageFormItemsGroup>

        <S.BusinessPageFormItemsGroup>
          <S.BusinessPageFormItemsPhone
            mask={createPhoneMask()}
            value={state?.phone}
            guide={false}
            id="phone"
            name="phone"
            placeholder=" "
            onChange={handleInputChange}
            autoComplete="off"
            data-stat="landing-form-phone"
            inputMode="numeric"
            pattern="[0-9]*"
            $hasError={!!errors.phone}
          />
          <S.BusinessPageFormLabel htmlFor="phone">
            {t('business.form.phone')}
          </S.BusinessPageFormLabel>
          <S.BusinessPageFormError>{errors.phone}</S.BusinessPageFormError>
        </S.BusinessPageFormItemsGroup>

        <S.BusinessPageFormItemsGroup>
          <S.BusinessPageFormItems
            id="email"
            name="email"
            onChange={handleInputChange}
            placeholder=" "
            data-stat="landing-form-email"
            $hasError={!!errors.email}
          />
          <S.BusinessPageFormLabel htmlFor="email" $showRequired={false}>
            {t('business.form.email')}
          </S.BusinessPageFormLabel>
          <S.BusinessPageFormError>{errors.email}</S.BusinessPageFormError>
        </S.BusinessPageFormItemsGroup>
      </S.BusinessPageFormItemsContainer>
      <S.BusinessPageFormButton
        onClick={handleFormSubmit}
        data-stat="landing-form-submit"
      >
        {t('business.send_form')}
      </S.BusinessPageFormButton>
      <S.BusinessPageFormContent>
        <Trans
          i18nKey="business.form.agree_gdpr_policy"
          components={{
            link1: (
              <CustomLink
                target={isLite ? '_self' : '_blank'}
                href={withAppFlag(ROUTES.terms, isLite)}
              />
            ),
          }}
        />
      </S.BusinessPageFormContent>
    </S.BusinessPageForm>
  );
};

BusinessPageForm.displayName = 'BusinessPageForm';
